// rulesetModel.js — Port von OC-Pilot frontend/src/ruleset/model.ts (664 LOC TS) nach JS,
// MOEGLICHST 1:1 (Bauplan-Auftrag, Agent FE-3, Welle 2 "UI-Paritaet"). Strukturiertes
// Formular-Modell fuer Regelsets + verlustfreie Serialisierung in das Engine-JSON
// (column_rows/indented_position_blocks) und zurueck. Reine Funktionen, OHNE React —
// describeField(fm, t) nimmt den Uebersetzer als Parameter entgegen (Precedent: ui.js#eventLabel),
// damit die Datei framework-los bleibt UND keine deutschen Literale hart codiert sind (RULES: i18n).
//
// Zielschema siehe ocr/rules/engine.py (_field_from_band, price_per_unit, isoweek_monday) und
// deploy/db/mysql/002_seed_rulesets.sql (OC-Pilot-Referenz, NUR gelesen).
//
// AUSNAHME 500-LOC-Regel: Datei-Umfang ist vom Bauplan ausdruecklich als 1:1-Port vorgesehen
// ("model.ts ist 664 LOC pure Logik und wird moeglichst 1:1 nach JS portiert") — analog zu den
// dokumentierten Altlasten App.js/CreateBanfView.js: additive Pflege statt Aufsplitten.
//
// DOKUMENTIERTE Abweichungen vom TS-Original (s. Verifikationsbericht des Agenten):
// 1) fromRulesetJson(raw) akzeptiert zusaetzlich ein BEREITS GEPARSTES Objekt (nicht nur einen
//    JSON-String) — noetig, weil GET /rulesets/:id (rulesetService.mapRow) rules_json je nach
//    Spaltentyp als JS-Objekt liefern kann; OC-Pilots eigenes Backend lieferte dort immer einen
//    String. toRulesetJson(form) liefert weiterhin einen String (fuer den JSON-Experten-Tab);
//    toRulesetObject(form) (NEU exportiert) liefert das reine Objekt fuer API-Aufrufe.
// 2) delivery-Objekt (Strategie 'blocks'): unbekannte Zusatzschluessel (z. B. "schedule"/
//    "placeholder" — von avnet.json TATSAECHLICH verwendet und in ocr/rules/engine.py
//    _schedule_lines() fachlich wirksam) werden NICHT wie im TS-Original stillschweigend
//    verworfen, sondern loesen markUnsupported() aus: das Formular kann sie nicht abbilden,
//    der JSON-Experten-Modus bleibt Quelle der Wahrheit — verhindert stillen Datenverlust beim
//    Speichern aus dem visuellen Editor heraus.

export const FIELDS = ['EBELN', 'EBELP', 'LABNR', 'MATNR', 'MFRPN', 'MENGE', 'NETPR', 'EEIND', 'WAERS'];

// ---- Default-Muster (unveraendert aus dem TS-Original) ----
export const DEFAULT_ANCHOR = '^\\d+$';
export const DEFAULT_START = '^\\s{2,}(\\d+)\\s*$'; // Positionsblock-Beginn: eingerueckte Positionsnummer
export const DEFAULT_YEAR = '\\b\\d{2}\\.\\d{2}\\.(\\d{4})\\b';
const P_DEC_DE = '([\\d.]*\\d(?:,\\d+)?)';
const P_DEC_US = '([\\d,]*\\d(?:\\.\\d+)?)';
const P_DMY = '(\\d{2}\\.\\d{2}\\.\\d{4})';
const P_KW_TEXT = '(\\d{1,2}/\\d{2,4})';
const P_KW_COLUMN = '(\\d{1,2})/'; // Schraegstrich verhindert Treffer auf Beschreibungs-Zahlen

/**
 * @typedef {Object} Box
 * @property {number} page @property {number} x0 @property {number} y0 @property {number} x1 @property {number} y1
 */
/**
 * @typedef {Object} FieldMapping
 * @property {string} field EBELN|EBELP|LABNR|MATNR|MFRPN|MENGE|NETPR|EEIND|WAERS
 * @property {'off'|'header'|'line'|'delivery'} scope
 * @property {'box'|'regex'|'presence'} headerMethod
 * @property {Box} [box]
 * @property {string} [regexPattern] @property {number} [group]
 * @property {string} [presenceValue] @property {string} [presencePattern]
 * @property {string} [headerFormat]
 * @property {'column'|'label'|'sequence'} lineMode
 * @property {number} [xMin] @property {number} [xMax]
 * @property {string} [linePattern]
 * @property {string} format text|decimal_de|decimal_us|date_dmy|date_isoweek_monday|date_mon_dmy|isoweek_week_column|price_per_unit|price_per_pe_code|clean
 * @property {number} [unitXMin] @property {number} [unitXMax] @property {number} [decimals]
 * @property {number} [lineGroup]
 * @property {string} [ppuPattern] @property {number} [ppuPriceGroup] @property {number} [ppuUnitGroup]
 * @property {number} [rowStart] @property {number} [rowStep]
 */
/**
 * @typedef {Object} RulesetForm
 * @property {string} supplierId @property {string} supplierName @property {string[]} detection
 * @property {number} [isoweekDay]
 * @property {'column_rows'|'blocks'} strategy
 * @property {number} page @property {boolean} allPages @property {number} bandPad
 * @property {number} [rowAnchorXMin] @property {number} [rowAnchorXMax] @property {string} rowAnchorPattern
 * @property {string|null} rowEnd @property {string[]} rowSkip @property {string} yearPattern
 * @property {string} startPattern @property {string} requirePattern
 * @property {string} deliveryAnchor @property {number} deliveryPairIndex
 * @property {string} deliveryDatePattern @property {string} deliveryNumPattern @property {string} deliveryStopPrefix
 * @property {Object.<string, FieldMapping>} fields
 * @property {boolean} unsupported @property {string} rawJson
 */

/** @returns {FieldMapping} */
function emptyField(field) {
  return { field, scope: 'off', headerMethod: 'box', lineMode: 'column', format: 'text' };
}

/** @returns {RulesetForm} */
export function emptyForm() {
  const fields = {};
  for (const f of FIELDS) fields[f] = emptyField(f);
  // Sinnvolle Voreinstellungen fuer die haeufigsten Felder
  fields.WAERS.scope = 'header';
  fields.WAERS.headerMethod = 'presence';
  fields.WAERS.presenceValue = 'EUR';
  return {
    supplierId: '', supplierName: '', detection: [],
    strategy: 'column_rows', page: 1, allPages: false, bandPad: 3,
    rowAnchorXMin: undefined, rowAnchorXMax: undefined, rowAnchorPattern: DEFAULT_ANCHOR,
    rowEnd: null, rowSkip: [], yearPattern: DEFAULT_YEAR,
    startPattern: DEFAULT_START, requirePattern: '',
    deliveryAnchor: '', deliveryPairIndex: 0,
    deliveryDatePattern: '', deliveryNumPattern: '', deliveryStopPrefix: '',
    fields, unsupported: false, rawJson: '',
  };
}

function normalizeForFormat(fmt) {
  switch (fmt) {
    case 'decimal_de': return 'decimal_de';
    case 'decimal_us': return 'decimal_us';
    case 'date_dmy': return 'date_dmy';
    case 'date_isoweek_monday': return 'date_isoweek_monday';
    case 'date_mon_dmy': return 'date_mon_dmy';
    case 'clean': return 'clean';
    default: return undefined;
  }
}

function defaultPatternForFormat(fmt) {
  switch (fmt) {
    case 'decimal_de': return P_DEC_DE;
    case 'decimal_us': return P_DEC_US;
    case 'date_dmy': return P_DMY;
    case 'date_isoweek_monday': return P_KW_TEXT;
    default: return undefined;
  }
}

// ---- Sinnvolle Voreinstellung beim Umschalten des Erfassungsorts (strategie-abhaengig). ----
// Vorbild MappingView.tsx#applyScope (dort lokal, hier exportiert — von AbMapFieldCard genutzt).
export function applyScope(fm, scope, strategy) {
  const next = { ...fm, scope };
  if (scope === 'header') {
    if (fm.field === 'WAERS') { next.headerMethod = 'presence'; if (next.presenceValue == null) next.presenceValue = 'EUR'; }
    else next.headerMethod = fm.headerMethod || 'regex';
    if (fm.field === 'EBELN' || fm.field === 'LABNR') next.headerMethod = 'regex';
  } else if (scope === 'line' && strategy === 'blocks') {
    // Block-Feld: textbasiert (Regex auf den Positionsblock) bzw. Preis÷Einheit.
    next.lineMode = 'label';
    next.format = fm.field === 'NETPR' ? 'price_per_unit' : 'text';
    if (next.lineGroup == null) next.lineGroup = 1;
  } else if (scope === 'line') {
    if (fm.field === 'MENGE') next.format = 'decimal_de';
    else if (fm.field === 'NETPR') next.format = 'price_per_unit';
    else if (fm.field === 'EEIND') next.format = 'isoweek_week_column';
    else next.format = next.format || 'text';
  }
  return next;
}

// ---- Form -> Engine-JSON ----
function headerSpec(fm) {
  const norm = fm.headerFormat ? normalizeForFormat(fm.headerFormat) : undefined;
  if (fm.headerMethod === 'box') {
    if (!fm.box) return null;
    const s = { method: 'box', ...fm.box };
    if (norm) s.normalize = norm;
    return s;
  }
  if (fm.headerMethod === 'presence') {
    const value = fm.presenceValue ?? '';
    if (!value) return null;
    return { method: 'presence', pattern: fm.presencePattern || `\\b${value}\\b`, value };
  }
  // regex
  if (!fm.regexPattern) return null;
  const s = { method: 'regex', pattern: fm.regexPattern, group: fm.group ?? 1 };
  if (norm) s.normalize = norm;
  return s;
}

function lineSpec(fm) {
  if (fm.lineMode === 'sequence') {
    return { method: 'row_number', start: fm.rowStart ?? 10, step: fm.rowStep ?? 10 };
  }
  if (fm.format === 'price_per_unit') {
    if (fm.xMin == null || fm.xMax == null || fm.unitXMin == null || fm.unitXMax == null) return null;
    return {
      method: 'price_per_unit',
      price: { x_min: fm.xMin, x_max: fm.xMax },
      unit: { x_min: fm.unitXMin, x_max: fm.unitXMax },
      decimals: fm.decimals ?? 6,
    };
  }
  if (fm.format === 'isoweek_week_column') {
    if (fm.xMin == null || fm.xMax == null) return null;
    return {
      method: 'isoweek_monday', x_min: fm.xMin, x_max: fm.xMax,
      pattern: fm.linePattern || P_KW_COLUMN, year_field: 'YEAR',
    };
  }
  const s = {};
  if (fm.lineMode === 'column') {
    if (fm.xMin == null || fm.xMax == null) return null;
    s.x_min = fm.xMin;
    s.x_max = fm.xMax;
  }
  const pat = fm.linePattern || defaultPatternForFormat(fm.format);
  if (pat) {
    s.pattern = pat;
    s.group = 1;
  } else if (fm.lineMode === 'label') {
    return null; // Label-Modus braucht ein Muster
  }
  const norm = normalizeForFormat(fm.format);
  if (norm) s.normalize = norm;
  return s;
}

// Block-Feld (indented_position_blocks): REGEX auf den Positionsblock-Text oder price_per_unit (regex-basiert).
function blockFieldSpec(fm) {
  if (fm.format === 'price_per_unit') {
    if (!fm.ppuPattern) return null;
    return {
      method: 'price_per_unit', pattern: fm.ppuPattern,
      price_group: fm.ppuPriceGroup ?? 1, unit_group: fm.ppuUnitGroup ?? 2, decimals: fm.decimals ?? 6,
    };
  }
  if (fm.format === 'price_per_pe_code') {
    if (!fm.ppuPattern) return null;
    return {
      method: 'price_per_pe_code', pattern: fm.ppuPattern,
      price_group: fm.ppuPriceGroup ?? 1, code_group: fm.ppuUnitGroup ?? 2, decimals: fm.decimals ?? 6,
    };
  }
  if (!fm.linePattern) return null;
  const s = { method: 'regex', pattern: fm.linePattern, group: fm.lineGroup ?? 1 };
  const norm = normalizeForFormat(fm.format);
  if (norm) s.normalize = norm;
  return s;
}

function blocksLineItems(form) {
  if (!form.startPattern) return {};
  const fields = {};
  for (const f of FIELDS) {
    const fm = form.fields[f];
    if (fm.scope !== 'line') continue;
    const spec = blockFieldSpec(fm);
    if (spec) fields[fm.field] = spec;
  }
  const li = { strategy: 'indented_position_blocks', start_pattern: form.startPattern };
  if (form.requirePattern) li.require = { regex: form.requirePattern };
  if (form.rowSkip.length) li.row_skip = form.rowSkip;
  li.fields = fields;
  // delivery liefert EEIND + MENGE gepaart; die Engine braucht BEIDE Felder -> nur dann emittieren.
  if (form.deliveryAnchor && form.fields.EEIND.scope === 'delivery' && form.fields.MENGE.scope === 'delivery') {
    const d = { anchor: form.deliveryAnchor, pair_index: form.deliveryPairIndex, eeind_field: 'EEIND', menge_field: 'MENGE' };
    if (form.deliveryDatePattern) d.date_pattern = form.deliveryDatePattern;
    if (form.deliveryNumPattern) d.num_pattern = form.deliveryNumPattern;
    if (form.deliveryStopPrefix) d.stop_prefix = form.deliveryStopPrefix;
    li.delivery = d;
  }
  return li;
}

/** Reines Engine-JSON-Objekt (fuer API-Aufrufe — createRuleset/previewRuleset erwarten rulesJson als Objekt ODER String). */
export function toRulesetObject(form) {
  const header_fields = {};
  for (const f of FIELDS) {
    const fm = form.fields[f];
    if (fm.scope !== 'header') continue;
    const spec = headerSpec(fm);
    if (spec) header_fields[f] = spec;
  }

  let line_items = {};
  if (form.strategy === 'blocks') {
    line_items = blocksLineItems(form);
  } else {
    const lineEntries = FIELDS.map((f) => form.fields[f]).filter((fm) => fm.scope === 'line');
    const usesKwColumn = lineEntries.some((fm) => fm.format === 'isoweek_week_column');
    if (usesKwColumn && !header_fields.YEAR) {
      header_fields.YEAR = { method: 'regex', pattern: form.yearPattern, group: 1 };
    }
    if (lineEntries.length && form.rowAnchorXMin != null && form.rowAnchorXMax != null) {
      const fields = {};
      for (const fm of lineEntries) {
        const spec = lineSpec(fm);
        if (spec) fields[fm.field] = spec;
      }
      line_items = {
        strategy: 'column_rows',
        page: form.allPages ? 'all' : form.page,
        band_pad: form.bandPad,
        row_anchor: { x_min: form.rowAnchorXMin, x_max: form.rowAnchorXMax, pattern: form.rowAnchorPattern },
        fields,
      };
      if (form.rowEnd) line_items.row_end = { pattern: form.rowEnd };
      if (form.rowSkip.length) line_items.row_skip = form.rowSkip;
    }
  }

  return {
    supplier_id: form.supplierId,
    supplier_name: form.supplierName,
    detection: { all_of: form.detection },
    // Liefertag der KW nur schreiben, wenn gesetzt (sonst Engine-Default Freitag).
    ...(form.isoweekDay != null ? { isoweek_day: form.isoweekDay } : {}),
    header_fields,
    line_items,
  };
}

/** JSON-String (fuer den Experten-Tab / Textarea) — unsupported: rawJson ist Quelle der Wahrheit. */
export function toRulesetJson(form) {
  if (form.unsupported) return form.rawJson;
  return JSON.stringify(toRulesetObject(form), null, 2);
}

// ---- Engine-JSON -> Form (Best-Effort) ----
// Normalizer, die das Formular verlustfrei abbilden kann. Ein unbekannter Normalizer ->
// markUnsupported (statt still auf 'text' zu degradieren und beim Speichern den Normalizer zu
// verlieren -> kaputter Zielwert).
const KNOWN_NORMALIZE = new Set(['decimal_de', 'decimal_us', 'date_dmy', 'date_isoweek_monday', 'date_mon_dmy', 'clean']);
function isUnknownNormalize(spec) {
  return typeof spec.normalize === 'string' && !KNOWN_NORMALIZE.has(spec.normalize);
}

function fmtFromNormalize(norm) {
  switch (norm) {
    case 'decimal_de': return 'decimal_de';
    case 'decimal_us': return 'decimal_us';
    case 'date_dmy': return 'date_dmy';
    case 'date_isoweek_monday': return 'date_isoweek_monday';
    case 'date_mon_dmy': return 'date_mon_dmy';
    case 'clean': return 'clean';
    default: return 'text';
  }
}

// Regelset nutzt Funktionen ausserhalb des visuellen Formulars (z. B. delivery mit unbekannten
// Zusatzschluesseln, unbekannter Normalizer) -> JSON ist die Quelle der Wahrheit. Lieferant-
// Identitaet (ID/Name/Erkennung) wird dennoch uebernommen, damit klar ist, WELCHES Regelset offen
// ist; die Feld-Cards werden neutralisiert (scope 'off'), um keine irrefuehrende Teil-Konfig zu zeigen.
function markUnsupported(rawJson, ident) {
  const f = emptyForm();
  for (const k of FIELDS) f.fields[k].scope = 'off';
  f.unsupported = true;
  f.rawJson = rawJson;
  if (ident) {
    f.supplierId = ident.supplierId;
    f.supplierName = ident.supplierName;
    f.detection = ident.detection;
  }
  return f;
}

// line_items.row_skip normalisieren: String, {pattern:...} oder Liste davon -> string[] (Regex-Muster).
function parseRowSkip(v) {
  if (!v) return [];
  const items = Array.isArray(v) ? v : [v];
  const out = [];
  for (const it of items) {
    const pat = typeof it === 'string' ? it : (it && typeof it === 'object' && typeof it.pattern === 'string') ? it.pattern : '';
    if (pat) out.push(pat);
  }
  return out;
}

// Schluessel, die das Formular im delivery-Objekt abbilden kann (Abweichung 2, s. Datei-Kopf):
// jeder ANDERE Schluessel (z. B. "schedule"/"placeholder") macht das Regelset 'unsupported'.
const KNOWN_DELIVERY_KEYS = new Set(['anchor', 'pair_index', 'date_pattern', 'num_pattern', 'stop_prefix', 'eeind_field', 'menge_field']);

/**
 * @param {string|Object} raw JSON-String ODER bereits geparstes Objekt (s. Abweichung 1, Datei-Kopf).
 * @returns {RulesetForm}
 */
export function fromRulesetJson(raw) {
  let rawJson;
  let obj;
  if (typeof raw === 'string') {
    rawJson = raw;
    try {
      obj = JSON.parse(raw);
    } catch {
      return markUnsupported(rawJson);
    }
  } else if (raw && typeof raw === 'object') {
    obj = raw;
    try { rawJson = JSON.stringify(raw, null, 2); } catch { rawJson = String(raw); }
  } else {
    return markUnsupported('');
  }

  const form = emptyForm();
  form.rawJson = rawJson;
  form.supplierId = String(obj.supplier_id ?? '');
  form.supplierName = String(obj.supplier_name ?? '');
  const det = obj.detection?.all_of;
  form.detection = Array.isArray(det) ? det.map(String) : [];
  if (typeof obj.isoweek_day === 'number') form.isoweekDay = obj.isoweek_day;
  // Identitaet auch im unsupported-Fall erhalten (zeigt, WELCHES Regelset offen ist).
  const ident = { supplierId: form.supplierId, supplierName: form.supplierName, detection: form.detection };

  // Kopf-Felder
  const hf = obj.header_fields || {};
  for (const [name, spec] of Object.entries(hf)) {
    if (name === 'YEAR') {
      if (typeof spec.pattern === 'string') form.yearPattern = spec.pattern;
      continue; // Hilfsfeld, kein Card
    }
    if (!FIELDS.includes(name)) return markUnsupported(rawJson, ident);
    const fm = form.fields[name];
    fm.scope = 'header';
    if (isUnknownNormalize(spec)) return markUnsupported(rawJson, ident);
    const method = spec.method ?? 'regex';
    if (method === 'box') {
      fm.headerMethod = 'box';
      fm.box = { page: Number(spec.page ?? 1), x0: Number(spec.x0), y0: Number(spec.y0), x1: Number(spec.x1), y1: Number(spec.y1) };
      fm.headerFormat = fmtFromNormalize(spec.normalize);
    } else if (method === 'presence') {
      fm.headerMethod = 'presence';
      fm.presenceValue = String(spec.value ?? '');
      fm.presencePattern = String(spec.pattern ?? '');
    } else if (method === 'regex') {
      fm.headerMethod = 'regex';
      fm.regexPattern = String(spec.pattern ?? '');
      fm.group = spec.group != null ? Number(spec.group) : 1;
      fm.headerFormat = fmtFromNormalize(spec.normalize);
    } else {
      return markUnsupported(rawJson, ident);
    }
  }

  // Positions-Strategie
  const li = obj.line_items || {};
  if (li && Object.keys(li).length) {
    // row_skip strategie-agnostisch lesen (String, {pattern} oder Liste davon) -> Round-trip-Erhalt.
    form.rowSkip = parseRowSkip(li.row_skip);
    if (li.strategy === 'indented_position_blocks') {
      form.strategy = 'blocks';
      form.startPattern = typeof li.start_pattern === 'string' ? li.start_pattern : '';
      const req = li.require;
      form.requirePattern = req && typeof req.regex === 'string' ? req.regex : '';

      const bf = li.fields || {};
      for (const [name, spec] of Object.entries(bf)) {
        if (!FIELDS.includes(name)) return markUnsupported(rawJson, ident);
        const fm = form.fields[name];
        fm.scope = 'line';
        fm.lineMode = 'label';
        if (isUnknownNormalize(spec)) return markUnsupported(rawJson, ident);
        const method = spec.method ?? 'regex';
        if (method === 'price_per_unit') {
          fm.format = 'price_per_unit';
          fm.ppuPattern = String(spec.pattern ?? '');
          fm.ppuPriceGroup = spec.price_group != null ? Number(spec.price_group) : 1;
          fm.ppuUnitGroup = spec.unit_group != null ? Number(spec.unit_group) : 2;
          fm.decimals = spec.decimals != null ? Number(spec.decimals) : 6;
        } else if (method === 'price_per_pe_code') {
          fm.format = 'price_per_pe_code';
          fm.ppuPattern = String(spec.pattern ?? '');
          fm.ppuPriceGroup = spec.price_group != null ? Number(spec.price_group) : 1;
          fm.ppuUnitGroup = spec.code_group != null ? Number(spec.code_group) : 2;
          fm.decimals = spec.decimals != null ? Number(spec.decimals) : 6;
        } else if (method === 'regex') {
          fm.format = fmtFromNormalize(spec.normalize);
          fm.linePattern = String(spec.pattern ?? '');
          fm.lineGroup = spec.group != null ? Number(spec.group) : 1;
        } else {
          return markUnsupported(rawJson, ident); // presence/box im Block -> nicht visuell abbildbar
        }
      }

      const d = li.delivery;
      if (d) {
        // Abweichung 2 (Datei-Kopf): unbekannte Zusatzschluessel (schedule/placeholder u. ae.)
        // sind fachlich wirksam (engine.py) und werden NICHT stillschweigend verworfen.
        const unknownKeys = Object.keys(d).filter((k) => !KNOWN_DELIVERY_KEYS.has(k));
        if (unknownKeys.length) return markUnsupported(rawJson, ident);
        form.deliveryAnchor = String(d.anchor ?? '');
        form.deliveryPairIndex = d.pair_index != null ? Number(d.pair_index) : 0;
        form.deliveryDatePattern = typeof d.date_pattern === 'string' ? d.date_pattern : '';
        form.deliveryNumPattern = typeof d.num_pattern === 'string' ? d.num_pattern : '';
        form.deliveryStopPrefix = typeof d.stop_prefix === 'string' ? d.stop_prefix : '';
        if (d.eeind_field === 'EEIND') form.fields.EEIND.scope = 'delivery';
        else if (d.eeind_field != null) return markUnsupported(rawJson, ident);
        if (d.menge_field === 'MENGE') form.fields.MENGE.scope = 'delivery';
        else if (d.menge_field != null) return markUnsupported(rawJson, ident);
      }
      return form;
    }
    if (li.strategy !== 'column_rows' || li.delivery) return markUnsupported(rawJson, ident);
    if (li.page === 'all') { form.allPages = true; form.page = 1; }
    else form.page = Number(li.page ?? 1);
    form.bandPad = Number(li.band_pad ?? 3);
    const ra = li.row_anchor || {};
    form.rowAnchorXMin = ra.x_min != null ? Number(ra.x_min) : undefined;
    form.rowAnchorXMax = ra.x_max != null ? Number(ra.x_max) : undefined;
    form.rowAnchorPattern = String(ra.pattern ?? DEFAULT_ANCHOR);
    const re = li.row_end;
    form.rowEnd = re && typeof re.pattern === 'string' ? re.pattern : null;

    const lf = li.fields || {};
    for (const [name, spec] of Object.entries(lf)) {
      if (!FIELDS.includes(name)) return markUnsupported(rawJson, ident);
      const fm = form.fields[name];
      fm.scope = 'line';
      if (isUnknownNormalize(spec)) return markUnsupported(rawJson, ident);
      const method = spec.method;
      if (method === 'price_per_unit') {
        const price = spec.price || {};
        const unit = spec.unit || {};
        fm.format = 'price_per_unit';
        fm.lineMode = 'column';
        fm.xMin = Number(price.x_min);
        fm.xMax = Number(price.x_max);
        fm.unitXMin = Number(unit.x_min);
        fm.unitXMax = Number(unit.x_max);
        fm.decimals = spec.decimals != null ? Number(spec.decimals) : 6;
      } else if (method === 'isoweek_monday') {
        fm.format = 'isoweek_week_column';
        fm.lineMode = 'column';
        fm.xMin = Number(spec.x_min);
        fm.xMax = Number(spec.x_max);
        if (typeof spec.pattern === 'string' && spec.pattern !== P_KW_COLUMN) fm.linePattern = spec.pattern;
      } else if (method === 'row_number') {
        fm.lineMode = 'sequence';
        fm.rowStart = spec.start != null ? Number(spec.start) : 10;
        fm.rowStep = spec.step != null ? Number(spec.step) : 10;
      } else if (method && method !== 'regex') {
        return markUnsupported(rawJson, ident); // box o.ae. in line_items -> nicht abbildbar
      } else {
        // x-Bereich oder Label + optionales Muster/Normalizer
        if (spec.x_min != null && spec.x_max != null) {
          fm.lineMode = 'column';
          fm.xMin = Number(spec.x_min);
          fm.xMax = Number(spec.x_max);
        } else {
          fm.lineMode = 'label';
        }
        fm.format = fmtFromNormalize(spec.normalize);
        const pat = typeof spec.pattern === 'string' ? spec.pattern : undefined;
        if (pat && pat !== defaultPatternForFormat(fm.format)) fm.linePattern = pat;
      }
    }
  }
  return form;
}

// ---- Klartext-Beschreibung je Feld (fuer die Card-Anzeige) — Uebersetzer t() als Parameter,
// damit die Datei framework-los + i18n-konform bleibt (Precedent ui.js#eventLabel). ----
const FORMAT_KEYS = [
  'text', 'decimal_de', 'decimal_us', 'date_dmy', 'date_isoweek_monday', 'date_mon_dmy',
  'isoweek_week_column', 'price_per_unit', 'price_per_pe_code', 'clean',
];
/** Uebersetzter Klartext eines Formats (Dropdown-Optionen + describeField). */
export function formatLabel(t, fmt) {
  return t(`ab.map.format.${fmt || 'text'}`);
}

export function describeField(fm, t) {
  if (fm.scope === 'off') return t('ab.map.describe.off');
  if (fm.scope === 'delivery') return t('ab.map.describe.delivery');
  if (fm.scope === 'header') {
    if (fm.headerMethod === 'box') return fm.box ? t('ab.map.describe.headerBox', { page: fm.box.page }) : t('ab.map.describe.headerBoxMissing');
    if (fm.headerMethod === 'presence') return t('ab.map.describe.headerPresence', { value: fm.presenceValue ?? '' });
    return fm.regexPattern ? t('ab.map.describe.headerRegex') : t('ab.map.describe.headerRegexMissing');
  }
  // line (Block-Regex oder column_rows)
  if (fm.lineMode === 'sequence') {
    return t('ab.map.describe.sequence', { start: fm.rowStart ?? 10, step: fm.rowStep ?? 10 });
  }
  if (fm.format === 'price_per_unit') {
    if (fm.lineMode === 'label') return fm.ppuPattern ? t('ab.map.describe.blockPpu') : t('ab.map.describe.blockPpuMissing');
    const ok = fm.xMin != null && fm.unitXMin != null;
    return ok ? t('ab.map.describe.columnPpu') : t('ab.map.describe.columnPpuMissing');
  }
  if (fm.format === 'price_per_pe_code') {
    return fm.ppuPattern ? t('ab.map.describe.blockPeCode') : t('ab.map.describe.blockPeCodeMissing');
  }
  if (fm.lineMode === 'label') {
    return fm.linePattern
      ? t('ab.map.describe.blockLabel', { group: fm.lineGroup ?? 1, format: formatLabel(t, fm.format) })
      : t('ab.map.describe.blockLabelMissing');
  }
  return fm.xMin != null
    ? t('ab.map.describe.column', { min: fm.xMin, max: fm.xMax, format: formatLabel(t, fm.format) })
    : t('ab.map.describe.columnMissing', { format: formatLabel(t, fm.format) });
}

// Formate/Methoden fuer Block-Felder (indented_position_blocks; textbasiert, keine Spalten/KW-Spalte).
export const BLOCK_FORMAT_VALUES = ['text', 'decimal_de', 'decimal_us', 'date_dmy', 'date_mon_dmy', 'date_isoweek_monday', 'clean', 'price_per_unit', 'price_per_pe_code'];

// Liste der Formate, die fuer Positionsfelder (Spalten-Strategie) im UI angeboten werden.
export const LINE_FORMAT_VALUES = ['text', 'decimal_de', 'decimal_us', 'date_dmy', 'date_mon_dmy', 'isoweek_week_column', 'date_isoweek_monday', 'price_per_unit'];

// Nur zur Vollstaendigkeit exportiert (z. B. fuer Validierung unbekannter Formate in Aufrufern).
export const ALL_FORMAT_VALUES = FORMAT_KEYS;
